import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import { PageHeader, DataTableCard } from '../ui';
import buildGASupportMessageColumns from './buildGASupportMessageColumns';
import SupportMessageDetailDialog from './SupportMessageDetailDialog';
import useGASupportMessagesPage from './useGASupportMessagesPage';

// The GA-side half of "Message support" (in-view help panel, step 9):
// every message sent through the panel lands here for GA to follow up on,
// in addition to the best-effort SUPPORT_INBOX_EMAIL notification.
const GASupportMessages = () => {
  const { i18n } = useTranslation();
  const p = useGASupportMessagesPage();

  const columns = buildGASupportMessageColumns({ t: p.t, i18n });

  return (
    <Box>
      <PageHeader
        title={p.t('ga.support.title', { defaultValue: 'Support messages' })}
        subtitle={p.t('{{count}} messages sent from the help panel', { count: p.total, defaultValue: `${p.total} messages sent from the help panel` })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('ga.support.search', { defaultValue: 'Search messages' }) }}
      />

      <DataTableCard
        status={p.status}
        rows={p.rows}
        columns={columns}
        empty={p.isNoMatch ? {
          kind: 'no-match',
          title: p.t('No messages match "{{query}}"', { query: p.search, defaultValue: `No messages match "${p.search}"` }),
          description: p.t('Try a different search.', { defaultValue: 'Try a different search.' }),
        } : {
          kind: 'none',
          icon: SupportAgentIcon,
          title: p.t('ga.support.empty', { defaultValue: 'No support messages yet' }),
          description: p.t('ga.support.emptyDescription', { defaultValue: 'Messages sent from the in-view help panel will show up here.' }),
        }}
        error={{
          title: p.t("Couldn't load messages", { defaultValue: "Couldn't load messages" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: () => p.fetchMessages(true),
        }}
        permission={{
          title: p.t('ga.error.forbidden'),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row._id}
        onRowClick={(params) => p.openDetail(params.row)}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <SupportMessageDetailDialog
        detail={p.detail}
        status={p.detailStatus}
        onClose={p.closeDetail}
        t={p.t}
        i18n={i18n}
      />
    </Box>
  );
};

export default GASupportMessages;
